import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  buildLocalizedUrl,
  getCurrentLanguage,
  LANGUAGES,
  SITE_URL,
} from '../../utils/languageUtils';

const BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'InteriorDesigner',
  name: 'SG Interior Design',
  url: 'https://interior-sg.com',
  logo: 'https://interior-sg.com/images/logo.png',
  image: 'https://interior-sg.com/images/og-default.jpg',
  telephone: '+359886275939',
  email: 'stefani.georgieva@interior-sg.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'ул. Стара Планина №34',
    addressLocality: 'Севлиево',
    postalCode: '5400',
    addressCountry: 'BG',
  },
  openingHours: 'Mo-Fr 10:00-18:00',
  sameAs: [
    'https://www.facebook.com/profile.php?id=61571604931438',
    'https://www.instagram.com/studio.sginterior',
    'https://www.tiktok.com/@sg.interior_design.bg',
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'Country',
    name: 'Bulgaria',
  },
};

export const SEO = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  article = false,
  noindex = false,
}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = getCurrentLanguage(i18n);

  // Canonical is derived from the route, never from window.location, so that
  // tracking parameters and the www host never spawn duplicate canonical URLs.
  const canonicalUrl = buildLocalizedUrl(lang, pathname);
  const alternateUrls = {
    [LANGUAGES.BG]: buildLocalizedUrl(LANGUAGES.BG, pathname),
    [LANGUAGES.EN]: buildLocalizedUrl(LANGUAGES.EN, pathname),
  };

  const siteUrl = SITE_URL;
  const siteName = 'SG Interior Design';
  const defaultImage = `${siteUrl}/images/og-default.jpg`;

  const pageTitle = title ? `${title} | ${siteName}` : siteName;
  const pageDescription =
    description ||
    'SG Interior Design - Интериорен дизайн и пространствено проектиране. Професионални услуги за жилищни и търговски интериори в България.';
  const pageImage = ogImage || defaultImage;
  const pageKeywords =
    keywords ||
    'интериорен дизайн, interior design, дизайн на интериор, обзавеждане, ремонт, проектиране, България';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="SG Interior Design" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Language alternates — reciprocal on both versions of every page */}
      <link rel="alternate" hrefLang="bg" href={alternateUrls.bg} />
      <link rel="alternate" hrefLang="en" href={alternateUrls.en} />
      <link rel="alternate" hrefLang="x-default" href={alternateUrls.bg} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={lang === 'bg' ? 'bg_BG' : 'en_US'} />
      {lang === 'bg' && <meta property="og:locale:alternate" content="en_US" />}
      {lang === 'en' && <meta property="og:locale:alternate" content="bg_BG" />}

      {/* Article specific tags */}
      {article && (
        <>
          <meta property="article:author" content="SG Interior Design" />
          <meta property="article:section" content="Interior Design" />
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional SEO */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#B8956B" />

      {/* Structured Data - LocalBusiness */}
      <script type="application/ld+json">{JSON.stringify(BUSINESS_SCHEMA)}</script>
    </Helmet>
  );
};
